<template>
  <div v-if="locationGambits.length > 0" class="location-gambits-tab">
    <div v-if="!hideHeader" class="sub-head upcoming-head">
      <h5>
        <GamIcon :name="GamIconName.PLAY_EMPTY" :size="GamIconSize.SMALL" is-current />
        {{ localize('locations.detail.upcoming') }}
      </h5>
      <hr />
    </div>

    <div class="gambits-list">
      <template v-for="gambit in locationGambits" :key="gambit.id">
        <GambitCardlet
          v-if="gambit.proposal === null || gambit.proposal?.isSuccessful"
          :gambit="gambit"
          :timezone="location.timezone || 'UTC'"
          @action-completed="handleCardletActionCompleted"
        />

        <ProposalCardlet
          v-else-if="gambit.proposal !== null && !gambit.proposal?.isSuccessful"
          :gambit="gambit"
          :timezone="location.timezone || 'UTC'"
          :is-invited="isGambitInvited(gambit)"
          :intent-id="getIntentIdForGambit(gambit)"
          @action-completed="handleCardletActionCompleted"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { BaseLocationDto, DetailLocationDto, NextGambitDto } from '@/core/data/location/location.interface';
import { localize } from '@/core/gambits';
import { useLocationStore } from '@/stores/location.store';
import { useSparkStore } from '@/stores/spark.store';
import { useTableStore } from '@/stores/table.store';
import GamIcon from '@/views/components/GamIcon.vue';
import GambitCardlet from '@/views/components/gam-gambits/GambitCardlet.vue';
import ProposalCardlet from '@/views/components/gam-gambits/ProposalCardlet.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import { computed, onMounted } from 'vue';

interface Props {
  location: DetailLocationDto;
  exclude?: NextGambitDto | null;
  hideHeader?: boolean;
}

const props = defineProps<Props>();

const sparkStore = useSparkStore();
const locationStore = useLocationStore();
const tableStore = useTableStore<BaseLocationDto>(GamListId.LOCATIONS)();

const locationGambits = computed(() => {
  if (!Array.isArray(props.location.nextGambits)) {
    return [];
  }

  return props.location.nextGambits.filter((g) => (props.exclude ? g.id !== props.exclude.id : true));
});

const hasProposals = computed(
  () => !!locationGambits.value.some((g) => g.proposal !== null && !g.proposal?.isSuccessful),
);

onMounted(() => {
  if (hasProposals.value) {
    sparkStore.ensureRespondInvitationsLoaded();
    if (!sparkStore.intents?.length) {
      sparkStore.fetchIntents();
    }
  }
});

const isGambitInvited = (gambit: NextGambitDto) => {
  if (gambit.proposal?.isInvited) return true;
  const proposalId = gambit.proposal?.id;
  if (!proposalId) return false;
  return (
    sparkStore.respondInvitedProposalIds.includes(proposalId) ||
    sparkStore.invitedProposals.some((p) => p.id === proposalId)
  );
};

const getIntentIdForGambit = (gambit: NextGambitDto) => {
  const dateKey = gambit.proposal?.date;
  if (!dateKey) return null;
  return sparkStore.intents?.find((intent) => intent.date === dateKey)?.id || null;
};

const handleCardletActionCompleted = async () => {
  if (props.location.id && locationStore.location?.id) {
    const result = await locationStore.fetch(props.location.id);
    if (result?.data?.nextGambits) {
      tableStore.updateItem({ id: props.location.id, nextGambits: result.data.nextGambits } as BaseLocationDto);
    }
  }
  sparkStore.fetchIntents({ refreshExisting: true });
};
</script>

<style scoped lang="scss">
.location-gambits-tab {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.gambits-list {
  display: flex;
  flex-direction: column;

  > :deep(*:not(:first-child)) {
    border-top: 1px solid var(--color-white-10);
  }
}

.upcoming-head h5 {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--color-white) !important;
  opacity: 1;

  .svg-container {
    display: flex;
    align-items: center;
  }
}
</style>
